import { useContext, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowLeft, Sparkles } from "lucide-react";
import { AuthContext } from "../Context/AuthContext";
import { hasRole } from "../../rbac/access";
import { PEOPLE_MODULES, PEOPLE_PATHS, peopleModuleFromPath } from "./peopleModules";
import "../../styles/peopleHub.css";

export function PeopleBackLink({ to = PEOPLE_PATHS.hub, label = "Back to People Hub" }) {
  return (
    <Link to={to} className="people-page-back">
      <ArrowLeft size={16} strokeWidth={2.2} aria-hidden />
      {label}
    </Link>
  );
}

function ModuleSwitch({ currentId }) {
  const { role } = useContext(AuthContext);
  const visible = useMemo(
    () => PEOPLE_MODULES.filter((m) => hasRole(role, m.roles)),
    [role]
  );
  if (visible.length < 2) return null;

  return (
    <nav className="people-page-switch" aria-label="People modules">
      {visible.map((mod) => {
        const Icon = mod.icon;
        const active = mod.id === currentId;
        return (
          <Link
            key={mod.id}
            to={mod.path}
            className={`people-page-switch-link${active ? " active" : ""}`}
            aria-current={active ? "page" : undefined}
            title={mod.desc}
          >
            <Icon size={14} />
            {mod.label}
          </Link>
        );
      })}
    </nav>
  );
}

export default function PeoplePageShell({
  eyebrow = "People",
  title,
  subtitle,
  icon: Icon,
  actions,
  stats,
  children,
  // eslint-disable-next-line no-unused-vars -- kept for callers; Layout's Back button is the only Back now.
  showBack = false,
  showSwitch = true,
}) {
  const { role } = useContext(AuthContext);
  const { pathname } = useLocation();
  const currentId = peopleModuleFromPath(pathname);

  return (
    <div className="people-page">
      <header className="people-hub-hero people-page-hero">
        <div className="people-hub-ambient" aria-hidden="true">
          <div className="people-hub-orb people-hub-orb--1" />
          <div className="people-hub-orb people-hub-orb--2" />
        </div>
        <div className="people-hub-hero-inner">
          <div className="people-page-hero-top">
            <div>
              <div className="people-hub-eyebrow">
                {Icon ? <Icon size={11} /> : <Sparkles size={11} />}
                {eyebrow}
              </div>
              <div className="people-hub-hero-row">
                <h1 className="people-hub-title">{title}</h1>
                {subtitle ? <p className="people-hub-sub">{subtitle}</p> : null}
              </div>
            </div>
            {actions ? <div className="people-page-hero-actions">{actions}</div> : null}
          </div>
          {stats ? <div className="people-page-hero-stats">{stats}</div> : null}
          {showSwitch ? <ModuleSwitch currentId={currentId} /> : null}
        </div>
      </header>

      <div className="people-page-body">{children}</div>
    </div>
  );
}
